// components/Billing/RestaurantsTab.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Stack,
  TextField,
  Button,
  MenuItem,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../../theme";
import moment from "moment";
import AddIcon from "@mui/icons-material/Add";

import { PaginationFooter, tableStyle } from "../../../components";
import { listAllRestaurantInvoices } from "../../../store/actions";

// ---------------------------------------------------------------------
// Helper: Format Unix timestamp
// ---------------------------------------------------------------------
const formatUnixDate = (timestamp) => {
  if (!timestamp) return "N/A";
  return moment.unix(timestamp).format("DD MMM YYYY");
};

// ---------------------------------------------------------------------
// DataGrid Columns
// ---------------------------------------------------------------------
const columns = [
  {
    field: "inv_date",
    headerName: "Invoice Date",
    flex: 1,
    valueGetter: (params) => formatUnixDate(params.value),
  },
  { field: "inv_no", headerName: "Invoice No.", flex: 1.2 },
  {
    field: "customer_name",
    headerName: "Customer Name",
    flex: 1,
  },
  {
    field: "total",
    headerName: "Amount",
    flex: 0.75,
    type: "number",
  },
  {
    field: "pmt_status",
    headerName: "Status",
    flex: 0.8,
    valueGetter: (params) =>
      params.value ? params.value.replace("_", " ").toUpperCase() : "N/A",
  },
];

// ---------------------------------------------------------------------
// Add Button
// ---------------------------------------------------------------------
const AddRestaurantButton = () => {
  const navigate = useNavigate();
  return (
    <Button
      variant="contained"
      startIcon={<AddIcon />}
      onClick={() => navigate("/billing/restaurant/new")}
      size="small"
    >
      Add Bill
    </Button>
  );
};

// ---------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------
const RestaurantsTab = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ------------------- Filter States -------------------
  const [invNo, setInvNo] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [pmtStatus, setPmtStatus] = useState("");

  // ------------------- Redux State -------------------
  const {
    list: restaurantInvoicesList = [],
    pageNumber = 1,
    pageSize = 20,
    totalCount = 0,
    showViewMore = false,
    isLoading = false,
  } = useSelector((state) => state.admin?.restaurantInvoice || {});

  // ------------------- Fetch Data -------------------
  const fetchMoreData = useCallback(
    ({ pageNumber: targetPage, pageSize: targetSize, reset = false }) => {
      const filters = {
        page_number: targetPage || 1,
        page_size: targetSize || pageSize,
      };

      // Apply current UI filters
      if (invNo?.trim()) filters.inv_no = invNo.trim();
      if (customerName?.trim()) filters.customer_name = customerName.trim();
      if (pmtStatus) filters.pmt_status = pmtStatus;
      if (dateFrom)
        filters.inv_date_from = moment(dateFrom).startOf("day").unix();
      if (dateTo) filters.inv_date_to = moment(dateTo).endOf("day").unix();

      // Dispatch with reset flag
      dispatch(
        listAllRestaurantInvoices({
          ...filters,
          __reset: reset, // custom flag
        })
      );
    },
    [dispatch, invNo, customerName, pmtStatus, dateFrom, dateTo, pageSize]
  );

  // Initial load
  useEffect(() => {
    fetchMoreData({ pageNumber: 1, reset: true });
  }, [fetchMoreData]);

  // ------------------- Handlers -------------------
  const handleSearch = () => {
    fetchMoreData({ pageNumber: 1, reset: true });
  };

  const handleReset = () => {
    setInvNo("");
    setDateFrom("");
    setDateTo("");
    setCustomerName("");
    setPmtStatus("");
    fetchMoreData({ pageNumber: 1, reset: true });
  };

  const handlePageChange = (newPage) => {
    fetchMoreData({ pageNumber: newPage });
  };

  const handlePageSizeChange = (newSize) => {
    fetchMoreData({ pageNumber: 1, pageSize: newSize, reset: true });
  };

  const handleViewInvoice = (params) => {
    navigate(`/billing/restaurants/${params.row._id}`);
  };

  // ------------------- Custom Footer -------------------
  const CustomFooter = () => (
    <PaginationFooter
      count={totalCount}
      pageSize={pageSize}
      pageNumber={pageNumber}
      fetchMoreData={(args) => {
        if (args.pageNumber) {
          handlePageChange(args.pageNumber);
        } else if (args.pageSize) {
          handlePageSizeChange(args.pageSize);
        }
      }}
    />
  );

  // -----------------------------------------------------------------
  return (
    <Box m="20px">
      <Typography variant="h6" gutterBottom>
        Restaurant Charges
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Add or review restaurant bills, taxes, and discounts here.
      </Typography>

      {/* Filter Bar */}
      <Stack
        direction="row"
        spacing={1}
        my={2}
        alignItems="center"
        flexWrap="wrap"
      >
        <TextField
          label="Invoice No."
          size="small"
          value={invNo}
          onChange={(e) => setInvNo(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
        />
        <TextField
          label="From Date"
          type="date"
          size="small"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            '& input[type="date"]::-webkit-calendar-picker-indicator': {
              filter:
                theme.palette.mode === "dark"
                  ? "invert(1)" // white in dark mode
                  : "invert(0)", // normal in light mode
            },
          }}
        />
        <TextField
          label="To Date"
          type="date"
          size="small"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            '& input[type="date"]::-webkit-calendar-picker-indicator': {
              filter:
                theme.palette.mode === "dark"
                  ? "invert(1)" // white in dark mode
                  : "invert(0)", // normal in light mode
            },
          }}
        />
        <TextField
          label="Customer Name"
          size="small"
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
        />
        <TextField
          select
          label="Payment Status"
          size="small"
          value={pmtStatus}
          onChange={(e) => setPmtStatus(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="unpaid">Unpaid</MenuItem>
          <MenuItem value="paid">Paid</MenuItem>
        </TextField>

        <Button variant="contained" onClick={handleSearch}>
          Search
        </Button>
        <Button variant="outlined" onClick={handleReset} size="small">
          Reset
        </Button>

        <Box sx={{ flexGrow: 1 }} />
        <AddRestaurantButton />
      </Stack>

      {/* DataGrid */}
      <Box sx={{ height: "68vh", width: "100%", ...tableStyle(colors) }}>
        <DataGrid
          loading={isLoading}
          rows={restaurantInvoicesList}
          columns={columns}
          components={{ Footer: CustomFooter }}
          getRowId={(row) => row._id}
          onRowClick={handleViewInvoice}
          sx={{
            "& .MuiDataGrid-row:hover": { cursor: "pointer" },
          }}
        />
      </Box>
    </Box>
  );
};

export default RestaurantsTab;
